// Camera framing for the character-creation preview.
//
// Pure and injectable so the arithmetic is testable without a WebGL context —
// which matters here because the bug this module exists to prevent is invisible
// in any environment where the canvas happens to be measured.
//
// The defect: framing divides by sin(hFov/2), and hFov is derived from
// camera.aspect. An UNMEASURED canvas reports aspect 0, so hFov is 0, sin(0) is
// 0, and the distance is Infinity — the camera is placed at infinity, three
// propagates NaN through the view matrix, and the preview renders nothing. It
// reads as "the camera flew away from the character".
//
// It is a race, not a constant: the framing runs after an awaited GLB load, so
// it lands before or after the canvas is measured depending on how fast the file
// parses. A local blob of a small rig is exactly the fast case.
//
// The rule, per the repo's no-silent-fallbacks policy: a degenerate viewport
// yields NO framing and says why, so the caller can defer and retry once a real
// size exists. It must never substitute a plausible-looking distance — a wrong
// framing is indistinguishable from a correct one to the reader, and would put
// the character somewhere arbitrary with no signal at all.

/** Smallest aspect we will trust. Below this the horizontal term explodes
    (radius/sin(hFov/2) → ∞) and the result is meaningless rather than merely
    imprecise. 0.01 is far below any real viewport and far above 0. */
const MIN_ASPECT = 0.01

/**
 * Distance at which a bounding sphere of `radius` fits both axes of the frustum.
 *
 * @param {object} p
 * @param {number} p.radius  bounding-sphere radius of the subject, world units
 * @param {number} p.fovDeg  camera's VERTICAL field of view, degrees
 * @param {number} p.aspect  viewport width / height
 * @param {number} [p.margin=1.1] slack so the subject is not flush to the edges
 * @returns {{ok: true, dist: number} | {ok: false, reason: string}}
 */
export function frameDistance({ radius, fovDeg, aspect, margin = 1.1 }) {
  if (!Number.isFinite(radius) || radius <= 0) {
    // An empty or degenerate bbox. Real cause seen in practice: a skinned mesh
    // whose geometry has no positions yet, so Box3.setFromObject yields an
    // empty box. Framing that would divide a zero radius into a zero angle.
    return { ok: false, reason: `subject radius is not usable (${radius})` }
  }
  if (!Number.isFinite(fovDeg) || fovDeg <= 0 || fovDeg >= 180) {
    return { ok: false, reason: `camera fov is not usable (${fovDeg})` }
  }
  if (!Number.isFinite(aspect) || aspect < MIN_ASPECT) {
    // THE bug this module exists for. Not clamped to a guess: the caller must
    // wait for a real measurement, because any distance chosen here would be
    // wrong for the viewport that eventually appears.
    return { ok: false, reason: `viewport not measured yet (aspect ${aspect})` }
  }

  const vFov = (fovDeg * Math.PI) / 180
  const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect)
  const dist = Math.max(
    radius / Math.sin(vFov / 2),
    radius / Math.sin(hFov / 2),
  ) * margin

  if (!Number.isFinite(dist) || dist <= 0) {
    // Belt and braces: every route to a non-finite dist is screened above, so
    // reaching here means an input shape nobody anticipated. Refusing beats
    // writing Infinity into a camera, which is unrecoverable — the NaN spreads
    // to the view matrix and every later frame renders nothing.
    return { ok: false, reason: `computed distance is not finite (${dist})` }
  }
  return { ok: true, dist }
}
